import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from '../../i18n'
import { useTheme } from '../../hooks/useTheme'
import CoachDashboard from './CoachDashboard'
import TraineeDashboard from './TraineeDashboard'
import QuickAccessBar from './QuickAccessBar'
import { UsernameWarning } from '../ui'
import { Avatar } from '../ui'
import { getShortMonthName } from '../../utils/dateTranslations'
import { openAssistantChat } from '../../utils/telegram'

type T = ReturnType<typeof useTranslation>['t']

function getGreeting(t: T): string {
  const hour = new Date().getHours()
  if (hour < 12) return t.dashboard.greetings.morning
  if (hour < 18) return t.dashboard.greetings.afternoon
  return t.dashboard.greetings.evening
}

function getDateLabel(t: T): string {
  const now = new Date()
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
  const dayName = t.common.day[dayKeys[now.getDay()]]
  const month = getShortMonthName(now, t).slice(0, 3)
  return `${dayName} ${now.getDate()} ${month}`
}

export default function Dashboard() {
  const { user, isLoading } = useAuth()
  const { t } = useTranslation()
  const theme = useTheme()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-5">
        <div className="text-base opacity-70">Loading...</div>
      </div>
    )
  }

  const displayName = user?.firstName || user?.username || (user?.hasTrainees ? 'Coach' : 'Trainee')
  const greeting = getGreeting(t)
  const dateLabel = getDateLabel(t)

  const header = (
    <header className="flex items-start justify-between mb-5">
      <div className="min-w-0 flex-1 pr-3">
        <p
          className="text-xs font-medium mb-1 tracking-wide"
          style={{ color: theme.text.muted }}
        >
          {dateLabel}
        </p>
        <h1
          className="text-2xl font-extrabold leading-tight"
          style={{ letterSpacing: '-0.4px', color: theme.text.primary }}
        >
          {greeting}, {displayName}
        </h1>
      </div>
      <Avatar
        photoUrl={user?.photoUrl}
        name={displayName}
        size="sm"
        fallbackBg={theme.buttonColor || theme.accent.text}
      />
    </header>
  )

  const assistantBanner = (
    <button
      onClick={openAssistantChat}
      className="w-full text-left rounded-xl px-3 py-2.5 mb-4 flex items-center gap-2 active:scale-[0.98] transition-transform"
      style={{
        backgroundColor: `${theme.buttonColor}12`,
        border: `1px solid ${theme.buttonColor}35`,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>🤖</span>
      <p className="text-xs flex-1 leading-snug" style={{ color: theme.text.muted }}>
        {t.dashboard.assistantBanner.title}
      </p>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor || theme.accent.text, flexShrink: 0 }}>
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  )

  if (user?.hasTrainees) {
    return (
      <div className="p-5">
        <UsernameWarning />
        {header}
        {assistantBanner}
        <CoachDashboard />
        <QuickAccessBar />
      </div>
    )
  }

  return (
    <div className="p-5">
      <UsernameWarning />
      {header}
      {assistantBanner}
      <TraineeDashboard />
      <QuickAccessBar />
    </div>
  )
}
